import { useCallback, useEffect, useState } from 'react'

import { validateSelectionReport } from '@/api/processing/services'
import { SelectionDivergence } from '@/api/processing/types'
import { DeedType, EducationStage } from '@/types/processing'
import { getErrorMessage, isThrottledError } from '@/utils/api'

import useEarlyUpload from './useEarlyUpload'

export type SelectionConsistencyStatus =
  | 'idle'
  | 'uploading'
  | 'checking'
  | 'valid'
  | 'divergent'
  | 'error'

export interface UseSelectionConsistencyParams {
  file: File | null
  nuDeedType: DeedType | null
  nuEducationStage: EducationStage | null
}

export interface UseSelectionConsistencyResult {
  status: SelectionConsistencyStatus
  divergences: SelectionDivergence[]
  uploadId: string | null
  uploadProgress: number
  canSubmit: boolean
  // Mensagem da API quando o `error` é um 429; senão, `null`.
  errorDetail: string | null
  retry: () => void
  reset: () => void
}

/**
 * PSV-352 — confere o relatório de escolha assim que ele é anexado.
 *
 * O upload foi antecipado do `handleSubmit` para o momento do anexo: antes não
 * havia arquivo no servidor para conferir, o que tornava impossível bloquear o
 * botão. O `uploadId` é reaproveitado no submit e só um `File` novo o invalida,
 * então trocar etapa ou tipo de obra revalida sem reenviar o arquivo, que
 * pode ter centenas de MB.
 *
 * Vive fora do modal para ser testável sem montar `Modal`/`Form`/`UploadFile`
 * do `@pnld/components-web`.
 */
const useSelectionConsistency = ({
  file,
  nuDeedType,
  nuEducationStage,
}: UseSelectionConsistencyParams): UseSelectionConsistencyResult => {
  const [status, setStatus] = useState<SelectionConsistencyStatus>('idle')
  const [divergences, setDivergences] = useState<SelectionDivergence[]>([])
  const [uploadProgress, setUploadProgress] = useState(0)
  const [errorDetail, setErrorDetail] = useState<string | null>(null)

  // Idempotente de propósito: o efeito roda a cada mudança de identidade do
  // `File`, e criar array novo toda vez dispararia render em cadeia.
  const goIdle = useCallback(() => {
    setUploadProgress(previous => (previous === 0 ? previous : 0))
    setDivergences(previous => (previous.length === 0 ? previous : []))
    setStatus(previous => (previous === 'idle' ? previous : 'idle'))
  }, [])

  const { uploadId, attempt, retry, reset, beginRequest, upload } =
    useEarlyUpload(goIdle)

  useEffect(() => {
    if (!file) {
      reset()
      return
    }

    // Falsy, e não só `null`: limpar o Select grava string vazia, e `Number('')`
    // é 0 — que não existe em `DeedType`/`EducationStage`, ambos começando em 1.
    if (!nuDeedType || !nuEducationStage) {
      goIdle()
      return
    }

    const request = beginRequest()

    const run = async () => {
      try {
        const currentUploadId = await upload(file, request, {
          onNewUpload: () => setUploadProgress(0),
          onUploading: () => setStatus('uploading'),
          onProgress: setUploadProgress,
        })
        if (currentUploadId === null) return

        setStatus('checking')

        const result = await validateSelectionReport({
          selectionUploadId: currentUploadId,
          nuDeedType,
          nuEducationStage,
        })

        if (!request.isCurrent()) return

        setDivergences(result.divergences ?? [])
        setStatus(result.valid ? 'valid' : 'divergent')
      } catch (error) {
        // Só a falha do upload invalida a promessa guardada, e o `upload` já
        // cuidou disso. Se quem falhou foi a conferência, o retry reaproveita
        // o arquivo que já subiu.
        if (!request.isCurrent()) return

        // Falha técnica não é "arquivo divergente": a tela diz coisas
        // diferentes nos dois casos.
        setDivergences([])
        setErrorDetail(isThrottledError(error) ? getErrorMessage(error) : null)
        setStatus('error')
      }
    }

    run()

    return request.cancel
  }, [
    file,
    nuDeedType,
    nuEducationStage,
    attempt,
    reset,
    goIdle,
    beginRequest,
    upload,
  ])

  return {
    status,
    divergences,
    uploadId,
    uploadProgress,
    // `error` libera de propósito: a conferência é conveniência de UI, e a
    // guarda que de fato impede um processamento divergente de nascer é o
    // `ProcessingCreateSerializer`, que roda a mesma regra e devolve 400. Se o
    // endpoint estiver fora, travar aqui tiraria uma capacidade que já existe.
    //
    // Com uma ressalva: se quem falhou foi o *upload*, não há `uploadId` e não
    // há o que enviar — aí o botão continua travado, senão ficaria habilitado
    // sem fazer nada ao ser clicado.
    canSubmit: status === 'valid' || (status === 'error' && uploadId !== null),
    errorDetail,
    retry,
    reset,
  }
}

export default useSelectionConsistency
